import {
  CheckboxEmpty,
  CheckboxFilled,
  CheckboxIndeterminate,
} from "@shared/ui/Inputs";

export const CHECKBOX_SIZES = {
  NORMAL: "normal",
  MEDIUM: "medium",
  SMALL: "small",
} as const;

/**
 * How a disabled checkbox is drawn.
 *
 * DASH is a filled box with a dash through it — "locked, and its value is not
 * the point". OUTLINE keeps the normal checked/unchecked shape and just mutes
 * it, for cases where the value still carries meaning and the box happens to be
 * unavailable: a shared-asset component a portal was never granted reads as
 * "off", not as "indeterminate".
 */
export const CHECKBOX_DISABLED_VARIANTS = {
  DASH: "dash",
  OUTLINE: "outline",
} as const;

type CheckboxSizeType = (typeof CHECKBOX_SIZES)[keyof typeof CHECKBOX_SIZES];
type CheckboxDisabledVariantType =
  (typeof CHECKBOX_DISABLED_VARIANTS)[keyof typeof CHECKBOX_DISABLED_VARIANTS];

const SIZE_STYLES: Record<CheckboxSizeType, string> = {
  [CHECKBOX_SIZES.NORMAL]: "h-2xl w-2xl",
  [CHECKBOX_SIZES.MEDIUM]: "h-xl w-xl",
  [CHECKBOX_SIZES.SMALL]: "h-lg w-lg",
};

// Explicit pixel sizes matching the tokens above (2xl=24, xl=20, lg=16).
// The SVG glyphs get these as width/height attributes so they render at the
// intended size even where the Tailwind sizing classes don't constrain them
// (e.g. inside the MRT table header/body cells).
const SIZE_PX: Record<CheckboxSizeType, number> = {
  [CHECKBOX_SIZES.NORMAL]: 24,
  [CHECKBOX_SIZES.MEDIUM]: 20,
  [CHECKBOX_SIZES.SMALL]: 16,
};

type CheckboxPropsType = {
  size?: CheckboxSizeType;
  checked: boolean;
  /**
   * Partial state (some-but-not-all of what this box stands for is ticked):
   * filled square with a white minus.
   */
  indeterminate?: boolean;
  disabled?: boolean;
  /** See CHECKBOX_DISABLED_VARIANTS. Defaults to the dash. */
  disabledVariant?: CheckboxDisabledVariantType;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
};

const Checkbox = ({
  size = CHECKBOX_SIZES.SMALL,
  checked,
  indeterminate = false,
  disabled = false,
  disabledVariant = CHECKBOX_DISABLED_VARIANTS.DASH,
  onClick = () => {},
}: CheckboxPropsType) => (
  <div
    className={`flex items-center justify-center ${
      disabled ? "" : "cursor-pointer"
    }`}
    {...(!disabled ? { onClick } : {})}
  >
    {disabled && disabledVariant === CHECKBOX_DISABLED_VARIANTS.DASH ? (
      // The dash is drawn smaller than the box itself, so it needs a wrapper
      // of the size every other state occupies — otherwise a disabled
      // checkbox shrinks and pulls its row out of line with its neighbours.
      <div
        className={`${SIZE_STYLES[size]} flex-shrink-0 flex items-center justify-center`}
      >
        <div
          className="w-md h-md flex items-center justify-center bg-secondary"
          style={{ borderRadius: "2px" }}
        >
          <div className=" h-px rounded bg-white" style={{ width: "70%" }} />
        </div>
      </div>
    ) : indeterminate && !checked ? (
      <CheckboxIndeterminate
        width={SIZE_PX[size]}
        height={SIZE_PX[size]}
        className={`${SIZE_STYLES[size]} flex-shrink-0 ${
          disabled ? "opacity-40" : ""
        }`}
      />
    ) : checked ? (
      <CheckboxFilled
        width={SIZE_PX[size]}
        height={SIZE_PX[size]}
        className={`${SIZE_STYLES[size]} flex-shrink-0 fill-secondary ${
          disabled ? "opacity-40" : ""
        }`}
      />
    ) : (
      <CheckboxEmpty
        width={SIZE_PX[size]}
        height={SIZE_PX[size]}
        className={`${SIZE_STYLES[size]} flex-shrink-0 ${
          disabled ? "opacity-60" : ""
        }`}
      />
    )}
  </div>
);

export default Checkbox;
